import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { orderBy } from 'lodash'

import { AnalyticsField, DuckDBSchemaType, FilterField } from '@types'
import { AnalyticsPosition } from './utils/types'

import { ANALYTICS_CAPTIONS } from '../utils/const'
import { ASSET_FIELD } from '@/const'

import { DuckDBService } from '@/services/duckdb'

import { getCurrentDate } from '@/helpers/dates'
import { getFilterValues } from '../utils/helpers'

import useAnalyticsStore from '../index'

export const TABLE_NAME = 'analytics_position'

const TABLE_SCHEMA: Record<string, DuckDBSchemaType> = {
  [AnalyticsField.DATE]: 'DATE',
  [AnalyticsField.ACCOUNT_ID]: 'VARCHAR',
  [AnalyticsField.ASSET_ID]: 'VARCHAR',
  [AnalyticsField.TAGS]: 'VARCHAR[]',
  [AnalyticsField.AMOUNT]: 'DOUBLE',
  [AnalyticsField.POSITION_CLOSE]: 'DOUBLE',
  [AnalyticsField.POSITION_COST]: 'DOUBLE',
  [AnalyticsField.POSITION_GAIN]: 'DOUBLE',
  [AnalyticsField.ASSET_CLOSE]: 'DOUBLE',
  [AnalyticsField.ASSET_COST]: 'DOUBLE',
  [AnalyticsField.ASSET_GAIN]: 'DOUBLE',
}

export default defineStore('analytics-position', () => {
  // INIT
  const duckdbInstance = DuckDBService.instance()

  const loading = ref(false)
  const deprecated = ref<boolean>()
  const trigger = ref(0)

  const datesRange = ref<string[]>([])

  // GETTERS

  const isInit = computed(() => deprecated.value !== undefined)

  const getDatesRange = computed(() => datesRange.value)

  // SETTERS
  const setList = async (data: { position_view: AnalyticsPosition[] }) => {
    if (deprecated.value === false) return
    if (!data.position_view) return
    deprecated.value = false
    datesRange.value = Array.from(new Set((data.position_view ?? []).map(item => item.date))).sort()
    await duckdbInstance.createTable(TABLE_NAME, TABLE_SCHEMA)
    await duckdbInstance.pushData(TABLE_NAME, data.position_view, TABLE_SCHEMA)
    trigger.value++
  }

  // ACTIONS

  const clear = () => {
    datesRange.value = []
    loading.value = false
    deprecated.value = undefined
    trigger.value = 0
  }

  return {
    isLoading: loading,
    isDeprecated: deprecated,

    isInit,
    trigger,

    getDatesRange,

    setList,

    clear,
  }
})

type UseAnalyticsPositionFiltersOptions = {
  scoped?: boolean
  withDate?: boolean
  withAmounts?: boolean
}

export const useAnalyticsPositionFilters = ({
  scoped = false,
  withDate = false,
  withAmounts = false,
}: UseAnalyticsPositionFiltersOptions = {}) => {
  const analyticsStore = useAnalyticsStore()

  const analyticsDate = computed(() => analyticsStore.getDate || getCurrentDate({}))

  const getValues = getFilterValues(TABLE_NAME, analyticsDate, scoped)

  const filters = computed(() => {
    const result: FilterField[] = []
    if (withDate) {
      result.push({
        name: AnalyticsField.DATE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.DATE],
        isDate: true,
      })
    }
    result.push(
      {
        name: AnalyticsField.ASSET_NAME,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET],
        getter: context => getValues(AnalyticsField.ASSET_ID, undefined, context),
      },
      {
        name: AnalyticsField.ASSET_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET_TYPE],
        getter: context => getValues(AnalyticsField.ASSET_ID, ASSET_FIELD.TYPE, context),
      },
    )
    if (withAmounts) {
      result.push(
        {
          name: AnalyticsField.AMOUNT,
          caption: ANALYTICS_CAPTIONS[AnalyticsField.AMOUNT],
          isAmount: true,
        },
        {
          name: AnalyticsField.ASSET_CLOSE,
          caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET_CLOSE],
          isAmount: true,
        },
        {
          name: AnalyticsField.POSITION_CLOSE,
          caption: ANALYTICS_CAPTIONS[AnalyticsField.POSITION_CLOSE],
          isAmount: true,
        },
      )
    }
    return orderBy(result, 'caption')
  })

  return {
    filters,
  }
}
